import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Spinner } from '@spark';

import { useAuth } from '@/features/auth/AuthContext.jsx';
import { HEARD_ABOUT_OPTIONS } from '@/shared/constants/profileOptions';

import { deriveCompanyFromEmail } from '../utils/companyFromEmail';
import { countryName } from '../utils/countries';
import { browserCountryCode } from '../utils/timezoneCountry';
import { markWelcomePending } from '../utils/welcomeFlag';
import CreateWorkspaceWizard, {
    IMPROVE_GOAL_OPTIONS,
    MODEL_AUDIENCE_OPTIONS,
    ONBOARDING_STEPS,
    completedStepFor,
    missingProfileSelects,
} from './CreateWorkspaceWizard';
import OnboardingLayout from './OnboardingLayout';

/**
 * Default `onCreated`: full reload to the home page so every cached view
 * starts from the new workspace. The flag set first makes the home page greet
 * the user with the welcome modal exactly once after that reload.
 */
export const goHome = () => {
    markWelcomePending();
    window.location.href = '/';
};

/**
 * The steps this user walks through.  Anyone who already belongs to a
 * workspace — invited into one, or the owner of one created on an earlier,
 * half-finished pass — never sees "Define your workspace": it exists, so the
 * step has nothing left to do (Figma 9224:4435 is the creation screen).
 * Only a user with no workspace at all keeps the full list.
 */
export function visibleOnboardingSteps(workspaces) {
    const hasWorkspace = Array.isArray(workspaces) && workspaces.length > 0;
    return hasWorkspace ? ONBOARDING_STEPS.filter((step) => step.id !== 'workspace') : ONBOARDING_STEPS;
}

const hasText = (value) => typeof value === 'string' && value.trim() !== '';
const hasKnownChoice = (values, options) => Array.isArray(values) && values.some((value) => options.includes(value));

/**
 * Pick the wizard step (index into `steps`) a returning user lands on: the
 * first visible step that is still pending, or the terms screen.
 *
 * `completed_step` is stored with the global numbering (about 1, workspace 2,
 * goals 3, heard_about 4, terms 5) regardless of which steps are visible, but
 * a step also counts as pending when the data it must produce is missing —
 * every step is mandatory, and the set of questions grew over time (company
 * name, role, company size, industry, "How did you hear about us?"), so a
 * user who saved an older, shorter version of a step answers it again:
 *
 * - about: `completed_step` < 1, or no name / country / company, or any of
 *   role / company size / industry unanswered (`identity` is the user's
 *   `{ name, country }`, the rest lives on `onboarding`);
 * - workspace: `completed_step` < 2, or no workspace exists (the
 *   `POST /workspaces` call failed after the profile was saved); the step is
 *   only in `steps` at all while the user has no workspace, so it can never
 *   hold back someone who already belongs to one;
 * - goals: `completed_step` < 3, or either multi-select is empty;
 * - heard_about: `completed_step` < 4, or no known answer.
 */
export function resolveInitialStep(onboarding, hasWorkspace, steps = ONBOARDING_STEPS, identity = {}) {
    const raw = Number(onboarding?.completed_step);
    const completed = Number.isFinite(raw) ? Math.max(Math.trunc(raw), 0) : 0;
    const done = (stepId) => completed >= completedStepFor(stepId);
    const pending = {
        about:
            !done('about') ||
            !hasText(identity?.name) ||
            !countryName(identity?.country) ||
            !hasText(onboarding?.company) ||
            missingProfileSelects(onboarding).length > 0,
        workspace: !done('workspace') || !hasWorkspace,
        goals:
            !done('goals') ||
            !hasKnownChoice(onboarding?.improve_goals, IMPROVE_GOAL_OPTIONS) ||
            !hasKnownChoice(onboarding?.model_audience, MODEL_AUDIENCE_OPTIONS),
        heard_about: !done('heard_about') || !HEARD_ABOUT_OPTIONS.includes(onboarding?.heard_about),
        terms: true,
    };
    const index = steps.findIndex((step) => pending[step.id]);
    return index === -1 ? steps.length - 1 : index;
}

/**
 * The workspace the wizard treats as "already defined": the one the user
 * owns (created on the workspace step) over any they were only invited to.
 */
export function pickExistingWorkspace(workspaces) {
    if (!Array.isArray(workspaces) || !workspaces.length) return null;
    return workspaces.find((ws) => ws?.is_owner) || workspaces[0];
}

/**
 * Company answers an invitee inherits as wizard defaults: the workspace's
 * `company` plus the owner's `company_size` / `industry`, which the bootstrap
 * puts on each `workspaces` row in the `onboarding` state.  Empty for someone
 * who owns a workspace (they answered these themselves) or has none.
 */
export function inheritedProfile(workspaces) {
    const joined = pickExistingWorkspace(workspaces);
    if (!joined || joined.is_owner) return {};
    return { company: joined.company || '', company_size: joined.company_size || '', industry: joined.industry || '' };
}

/**
 * `/create-workspace` — the onboarding wizard every new (non-god) account
 * completes once (Figma "MVP-DESIGNES" onboarding screens).  `AuthContext`
 * sends the user here when `POST /users/bootstrap` answers
 * `state: "no_workspace"` with `platform_role: "creator"`, or
 * `state: "onboarding"` for any account that has not accepted the terms yet
 * (including invitees who already belong to a workspace).  Gods go to
 * `/admin` and waiting-list applicants to `/waitlist/apply` instead, so no
 * role check is repeated here.
 *
 * Rendered as a standalone full-page route (outside the sidebar shell) on
 * `OnboardingLayout`.  Each step is persisted on the user document, so the
 * wizard resumes at `user.onboarding.completed_step`.  "Continue to Specific"
 * reloads to `/` so the session cookie (now carrying `workspace_id`) is
 * picked up by the bootstrap.
 */
export default function CreateWorkspacePage({ onCreated = goHome }) {
    const navigate = useNavigate();
    const { user, loading, isAuthEnabled, workspaces } = useAuth();

    useEffect(() => {
        if (loading) return;
        if (isAuthEnabled && !user) navigate('/login', { replace: true });
    }, [loading, isAuthEnabled, user, navigate]);

    if (loading || (isAuthEnabled && !user)) {
        return (
            <OnboardingLayout>
                <div className="onboarding-loading">
                    <Spinner size="large" aria-label="Loading" />
                </div>
            </OnboardingLayout>
        );
    }

    const onboarding = user?.onboarding || null;
    const existingWorkspace = pickExistingWorkspace(workspaces);
    const steps = visibleOnboardingSteps(workspaces);
    // `identity` is what the user saved; the browser time zone only pre-fills
    // the "From" dropdown and never counts as an answered step.
    const identity = { name: user?.name || '', country: user?.country || '' };

    return (
        <CreateWorkspaceWizard
            key={user?.email || 'anonymous'}
            steps={steps}
            initialName={identity.name}
            initialCompany={deriveCompanyFromEmail(user?.email)}
            initialCountry={identity.country || browserCountryCode()}
            initialStep={resolveInitialStep(onboarding, Boolean(existingWorkspace), steps, identity)}
            initialProfile={{
                role: onboarding?.role || '',
                company: onboarding?.company || '',
                company_size: onboarding?.company_size || '',
                industry: onboarding?.industry || '',
            }}
            defaultProfile={inheritedProfile(workspaces)}
            initialGoals={Array.isArray(onboarding?.improve_goals) ? onboarding.improve_goals : []}
            initialAudience={Array.isArray(onboarding?.model_audience) ? onboarding.model_audience : []}
            initialHeardAbout={onboarding?.heard_about || ''}
            initialTermsAccepted={Boolean(onboarding?.terms_accepted_at)}
            existingWorkspace={existingWorkspace}
            onCreated={onCreated}
        />
    );
}
